/**
 * ROROS Outcome status helpers — AC-FW3a-2-M-helpers
 *
 * Extracted from AnnualOutcomesPage.tsx and MeetingWizardPage.tsx to
 * eliminate duplicate inline definitions (same statusVariant logic in both files).
 * Single source of truth for outcome + work item badge variants and labels.
 */

import { t } from '../i18n.js';

// ─── Outcome status helpers ────────────────────────────────────────────────────

/**
 * Returns the Badge variant for an outcome status.
 * AC-FW3a-2-M-helpers.
 */
export function outcomeStatusVariant(status: string): 'success' | 'warning' | 'error' | 'default' {
  switch (status) {
    case 'achieved': return 'success';
    case 'missed':   return 'error';
    case 'archived': return 'default';
    default:         return 'warning'; // 'active'
  }
}

/**
 * Returns a translated display label for an outcome status.
 * AC-FW3a-2-M-i18n: wraps status via t() instead of raw string rendering.
 */
export function outcomeStatusLabel(status: string): string {
  switch (status) {
    case 'active':   return t('Active');
    case 'achieved': return t('Achieved');
    case 'missed':   return t('Missed');
    case 'archived': return t('Archived');
    default:         return status;
  }
}

// ─── Cascade work item status helpers ─────────────────────────────────────────

/**
 * Returns the Badge variant for a cascade work item status.
 * AC-FW3a-2-M-helpers + AC-FW3a-2-LOW-7: correct variant for all statuses.
 * AC-FW3a-3-2: adds quality_confirmation (warning) and escalated (error).
 *
 * Full 8-status coverage:
 *   done, released         → success
 *   blocked, escalated     → error   (escalation = critical, same severity as blocked)
 *   in_progress, ready,
 *   quality_confirmation   → warning (in-flight states requiring human attention)
 *   backlog                → default (default branch)
 */
export function workItemStatusVariant(status: string): 'success' | 'warning' | 'error' | 'default' {
  switch (status) {
    case 'done':
    case 'released':             return 'success';
    case 'blocked':
    case 'escalated':            return 'error';
    case 'in_progress':
    case 'ready':
    case 'quality_confirmation': return 'warning';
    default:                     return 'default'; // 'backlog'
  }
}

/**
 * Returns a translated display label for a cascade work item status.
 * AC-FW3a-2-M-i18n: wraps status via t() instead of raw string rendering.
 * AC-FW3a-3-2: adds quality_confirmation and escalated labels.
 */
export function workItemStatusLabel(status: string): string {
  switch (status) {
    case 'backlog':              return t('Backlog');
    case 'ready':                return t('Ready');
    case 'in_progress':          return t('In Progress');
    case 'done':                 return t('Done');
    case 'released':             return t('Released');
    case 'blocked':              return t('Blocked');
    case 'quality_confirmation': return t('Quality Confirmation');
    case 'escalated':            return t('Escalated');
    default:                     return status;
  }
}
